import React, { useEffect, useMemo, useRef, useState } from 'react';
import Header from '../Common_Files/Header';
import Footer from '../Common_Files/Footer';
import Select from 'react-select';
import config from '../Common_Files/Config';
import Swal from 'sweetalert2';

const ProofOfDelivery = () => {
    const [accessToken, setAccessToken] = useState(null);
    const [salesOrders, setSalesOrders] = useState([]);
    const [selectedSO, setSelectedSO] = useState(null);
    const [files, setFiles] = useState([]);
    const [proofList, setProofList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    // Add file input ref
    const fileInputRef = useRef(null);

    const API_BASE_URL = config.api_url + '/';

    // Check authentication on component mount
    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            Swal.fire({
                icon: 'error',
                title: 'Authentication Required',
                text: 'Please login to continue',
                confirmButtonText: 'OK'
            }).then(() => {
                window.location.href = '/login';
            });
            return;
        }
        setAccessToken(token);
    }, []);

    // Fetch sales orders when component mounts
    const fetchSalesOrders = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${API_BASE_URL}ws-get-sales-orders-dropdown`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            const result = await response.json();

            if (result.status === 'success') {
                setSalesOrders(result.data);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to fetch sales orders: ' + result.message,
                    confirmButtonText: 'OK'
                });
                setError('Failed to fetch sales orders: ' + result.message);
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch sales orders',
                confirmButtonText: 'OK'
            });
            setError('Failed to fetch sales orders');
            console.error('Error fetching sales orders:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (accessToken) {
            fetchSalesOrders();
        }
    }, [accessToken]);

    // Fetch proof of delivery list
    const fetchProofList = async () => {
        try {
            const searchValue = document.getElementById('search_proof') ? document.getElementById('search_proof').value : '';
            
            const formData = new FormData();
            // formData.append('sales_order_id', selectedSO ? selectedSO.value : '');
            formData.append('search', searchValue);

            const response = await fetch(`${API_BASE_URL}ws-get-proof-of-delivery`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                body: formData
            });

            const result = await response.json();

            if (result.status === 'success') {
                setProofList(result.data);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to fetch proof list: ' + result.message,
                    confirmButtonText: 'OK'
                });
                setError('Failed to fetch proof list: ' + result.message);
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch proof list',
                confirmButtonText: 'OK'
            });
            setError('Failed to fetch proof list');
            console.error('Error fetching proof list:', err);
        }
    };

    // Fetch proof list when component mounts or sales order changes
    useEffect(() => {
        if (accessToken) {
            fetchProofList();
        }
    }, [accessToken, selectedSO]);

    // Handle search input change
    const handleSearchChange = (e) => {
        fetchProofList();
    };

    // Handle file input change
    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);

        // Validate file types - only PDF allowed
        const invalidFiles = selectedFiles.filter(file => file.type !== 'application/pdf');

        if (invalidFiles.length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File Type',
                text: 'Please upload only PDF files. Other file types are not allowed.',
                confirmButtonText: 'OK'
            });
            setError('Please upload only PDF files. Other file types are not allowed.');
            return;
        }

        // Validate file sizes (5MB limit)
        const oversizedFiles = selectedFiles.filter(file => file.size > 10 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'File Too Large',
                text: 'Some files are too large. Maximum file size is 10MB.',
                confirmButtonText: 'OK'
            });
            setError('Some files are too large. Maximum file size is 10MB.');
            return;
        }

        setError(null);
        setFiles((prev) => [...prev, ...selectedFiles]);
        
        // Swal.fire({
        //     icon: 'success',
        //     title: 'Files Added',
        //     text: `${selectedFiles.length} PDF file(s) added successfully!`,
        //     timer: 2000,
        //     showConfirmButton: false
        // });
    };

    // Generate file previews
    const filePreviews = useMemo(
        () =>
            files.map((file, idx) => (
                <li key={`${file.name}-${idx}`} style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                    {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        style={{
                            marginLeft: '10px',
                            background: 'none',
                            border: 'none',
                            color: 'red',
                            cursor: 'pointer'
                        }}
                    >
                        ✕
                    </button>
                </li>
            )),
        [files]
    );

    // Remove file from list
    const removeFile = (indexToRemove) => {
        Swal.fire({
            title: 'Remove File',
            text: 'Are you sure you want to remove this file?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6 !important',
            cancelButtonColor: '#d33 !important',
            confirmButtonText: 'Yes, remove it!'
        }).then((result) => {
            if (result.isConfirmed) {
                setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
                Swal.fire({
                    icon: 'success',
                    title: 'File Removed',
                    text: 'File has been removed from the list.',
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedSO) {
            Swal.fire({
                icon: 'warning',
                title: 'Selection Required',
                text: 'Please select a sales order',
                confirmButtonText: 'OK'
            });
            setError('Please select a sales order');
            return;
        }

        if (files.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Files Required',
                text: 'Please select at least one PDF file',
                confirmButtonText: 'OK'
            });
            setError('Please select at least one file');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setSuccess(null);

        const formData = new FormData();
        formData.append('sales_order_id', selectedSO.value);
        formData.append('uploaded_by', 1); // You can get this from user context

        files.forEach((file, index) => {
            formData.append(`files[${index}]`, file);
        });

        try {
            const response = await fetch(`${API_BASE_URL}ws-add-proof-of-delivery`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                body: formData
            });

            const result = await response.json();

            if (result.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Proof of delivery uploaded successfully!',
                    confirmButtonText: 'OK'
                });
                setSuccess('Proof of delivery uploaded successfully!');
                setFiles([]);
                setSelectedSO(null);
                // Clear file input field
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }

                // Refresh the sales orders dropdown
                await fetchSalesOrders();
                // Refresh the proof list
                await fetchProofList();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Upload Failed',
                    text: result.message || 'Failed to upload proof of delivery',
                    confirmButtonText: 'OK'
                });
                setError(result.message || 'Failed to upload proof of delivery');
                if (result.errors) {
                    console.error('Upload errors:', result.errors);
                }
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to submit proof of delivery. Please try again.',
                confirmButtonText: 'OK'
            });
            setError('Failed to submit proof of delivery. Please try again.');
            console.error('Submit error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle file download
    const handleDownload = async (proofItem) => {
        try {
            const response = await fetch(proofItem.download_url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = proofItem.file_name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                Swal.fire({
                    icon: 'success',
                    title: 'Download Started',
                    text: 'File download has started.',
                    timer: 1500,
                    showConfirmButton: false
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Download Failed',
                    text: 'Failed to download file',
                    confirmButtonText: 'OK'
                });
                setError('Failed to download file');
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Download Failed',
                text: 'Failed to download file',
                confirmButtonText: 'OK'
            });
            setError('Failed to download file');
            console.error('Download error:', err);
        }
    };

    // Handle file view
    const handleView = (proofItem) => {
        try {
            window.open(proofItem.download_url, '_blank');
            // Swal.fire({
            //     icon: 'success',
            //     title: 'File Opened',
            //     text: 'File has been opened in a new tab.',
            //     timer: 1500,
            //     showConfirmButton: false
            // });
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'View Failed',
                text: 'Failed to view file',
                confirmButtonText: 'OK'
            });
            setError('Failed to view file');
            console.error('View error:', err);
        }
    };

    // Handle delete proof
    const handleDelete = async (proofId) => {
        const result = await Swal.fire({
            title: 'Delete Proof of Delivery',
            text: 'Are you sure you want to delete this proof of delivery? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33 !important', //red shade
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}ws-delete-proof-of-delivery`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    proof_id: proofId
                })
            });

            const deleteResult = await response.json();

            if (deleteResult.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Proof of delivery deleted successfully!',
                    confirmButtonText: 'OK'
                });
                setSuccess('Proof of delivery deleted successfully!');
                await fetchProofList();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Delete Failed',
                    text: deleteResult.message || 'Failed to delete proof of delivery',
                    confirmButtonText: 'OK'
                });
                setError(deleteResult.message || 'Failed to delete proof of delivery');
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Delete Failed',
                text: 'Failed to delete proof of delivery',
                confirmButtonText: 'OK'
            });
            setError('Failed to delete proof of delivery');
            console.error('Delete error:', err);
        }
    };

    // Don't render if not authenticated
    if (!accessToken) {
        return <div>Checking authentication...</div>;
    }

    return (
        <div>
            <Header />

            <div className="breadcrumbs">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12 col-lg-12 col-sm-12">
                            <a href="#"><i className="fa fa-home"></i></a> &gt;
                            <a href="/Dashboard"> Dashboard</a> &gt;
                            <span className="last-crumb"> Proof Of Delivery</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="main-container">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12 col-lg-12 col-sm-12">
                            <div className="dashboard">
                                <div className="dashform">
                                    {/* {success && (
                                        <div className="alert alert-success" role="alert">
                                            {success}
                                        </div>
                                    )}

                                    {error && (
                                        <div className="alert alert-danger" role="alert">
                                            {error}
                                        </div>
                                    )} */}

                                    <form onSubmit={handleSubmit}>
                                        <div className="topsearch-area">
                                            <div className="row">
                                                <div className="col-12 col-lg-8 col-sm-8">
                                                    <div className="row">
                                                        <label className="col-sm-3 col-form-label">
                                                            Sales Order <span style={{ color: 'red' }}>*</span>
                                                        </label>
                                                        <div className="col-sm-7">
                                                            <Select
                                                                isClearable
                                                                name="sales_order_id"
                                                                options={salesOrders}
                                                                placeholder={isLoading ? "Loading..." : "Select Sales Order"}
                                                                value={selectedSO}
                                                                onChange={(value) => {
                                                                    setSelectedSO(value);
                                                                    setError(null);
                                                                }}
                                                                isDisabled={isSubmitting || isLoading}
                                                                isLoading={isLoading}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-lg-8 col-sm-8 mt-2">
                                                    <div className="row">
                                                        <label className="col-sm-3 col-form-label">
                                                            Upload Document <span style={{ color: 'red' }}>*</span>
                                                        </label>
                                                        <div className="col-sm-7">
                                                            <input
                                                                type="file"
                                                                multiple
                                                                accept=".pdf"
                                                                onChange={handleFileChange}
                                                                className="form-control"
                                                                disabled={isSubmitting}
                                                                ref={fileInputRef}
                                                            />
                                                            <small className="form-text text-muted">
                                                                Allowed format: PDF only (Max 10MB per file)
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-lg-8 col-sm-8 d-flex justify-content-start">
                                                    <button
                                                        type="submit"
                                                        className="popup-innerbtn-common"
                                                        disabled={!selectedSO || files.length === 0 || isSubmitting}
                                                    >
                                                        {isSubmitting ? 'Submitting...' : 'Submit'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>

                                    <div className="topsearch-area mt-3">
                                        <div className="row">
                                            <div className="col-12 col-lg-4 col-sm-4">
                                                <div className="row">
                                                    <label className="col-sm-2 col-form-label">Search</label>
                                                    <div className="col-sm-10">
                                                        <input 
                                                            type="text" 
                                                            className="search-area form-control" 
                                                            autoComplete='off' 
                                                            onChange={handleSearchChange}
                                                            placeholder="Search" 
                                                            name="search_proof" 
                                                            id="search_proof" 
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="verical-scrl-wrapper common-table" id="style-3">
                                        <table className="table table-bordered dataTable mt-2">
                                            <thead>
                                                <tr>
                                                    <th>Sr No</th>
                                                    <th>Sales Order</th>
                                                    <th>Customers</th>
                                                    <th>File Name</th>
                                                    <th>File Size</th>
                                                    <th>Upload Date</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {proofList.length === 0 ? (
                                                    <tr style={{ textAlign: 'center' }}>
                                                        <td colSpan="7">No proof uploaded</td>
                                                    </tr>
                                                ) : (
                                                    proofList.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>{index + 1}</td>
                                                            <td>{item.reference_no}</td>
                                                            <td>{item.customer_name}</td>
                                                            <td>{item.file_name}</td>
                                                            <td>{item.file_size_mb} MB</td>
                                                            <td>{item.uploaded_at_formatted}</td>
                                                            <td>
                                                                {/* <a
                                                                    href="#"
                                                                    className="applybtn me-2"
                                                                    title="Download"
                                                                    onClick={e => { e.preventDefault(); handleDownload(item); }}
                                                                >
                                                                    <i className="fa fa-download"></i>
                                                                </a> */}
                                                                <a
                                                                    href="#"
                                                                    className="applybtn me-2"
                                                                    title="View"
                                                                    onClick={e => { e.preventDefault(); handleView(item); }}
                                                                >
                                                                    <i className="fa fa-eye"></i>
                                                                </a>
                                                                <a
                                                                    href="#"
                                                                    className="deductionbtn"
                                                                    title="Delete"
                                                                    onClick={e => { e.preventDefault(); handleDelete(item.id); }}
                                                                >
                                                                    <i className="fa fa-trash"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ProofOfDelivery;